import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import {
  getPromoPopups,
  postPromoPopupDisplay,
} from "../../utils/APICalls/APICalls";
import {
  PopupStage,
  PromoPopupDirective,
} from "../../utils/promoMaterial/promoMaterialTypes";
import {
  recordDisplay,
  shownParam,
} from "../../utils/promoMaterial/promoPopupHistory";
import {
  PromoPopupResolutionKind,
  durableDisplayOf,
  resolvePromoPopup,
} from "../../utils/promoMaterial/resolvePromoPopup";
import { useLoyaltyContext } from "../hooks/loyalty/useLoyaltyContext";
import { useModalHook } from "../hooks/useModal";
import { usePromoActionHandler } from "../hooks/promoMaterial/usePromoActionHandler";
import { useAppSelector } from "../hooks/useReduxHooks";
import { selectCustomerLoyaltyOffers } from "../redux/selectors/loyaltyOfferSelectors";
import { selectLoadedTierIds } from "../redux/selectors/loyaltyServiceSelectors";

/** What a stage ask needs beyond the stage itself. */
type AskForStageOptions = {
  /** CHECKOUT: the server suppresses its nudge outright when one is applied. */
  rewardApplied?: boolean;
  /** The offer the customer arrived for — how SIGN_IN decides. */
  advertisedOfferId?: string;
};

type PromoPopupContextValue = {
  /**
   * Ask the server what belongs at [stage] and show it. Resolves to whether
   * anything was actually put on screen — checkout uses that to hold the
   * customer on the cart instead of navigating past the nudge.
   */
  askForStage: (
    stage: PopupStage,
    options?: AskForStageOptions
  ) => Promise<boolean>;
};

const PromoPopupContext = createContext<PromoPopupContextValue>({
  askForStage: () => Promise.resolve(false),
});

/**
 * SIGN_IN is the one stage the server hands out across queries — up to two
 * pop-ups in sequence — so a round that showed something asks again. The session
 * throttle empties the answer well before this bound; it only caps a misbehaving
 * server.
 */
const MAX_SIGN_IN_ROUNDS = 3;

/**
 * Shows the pop-ups the loyalty service picked for a moment of the journey, in
 * its order, one at a time.
 *
 * Sequencing is the interesting part. This app's modals are a single Redux slot
 * with a shared close control, so there is no dialog promise to await: the
 * provider turns "wait until this one is dismissed" into a promise resolved by
 * watching that slot, which lets the orchestrator below read as a plain
 * sequential loop — the same shape the mobile client uses.
 */
export const PromoPopupProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { handleModal } = useModalHook();
  const { channel, mappedOrderType, selectedFranchiseId, restaurantId } =
    useLoyaltyContext();
  const offers = useAppSelector(selectCustomerLoyaltyOffers);
  const restaurantConfig = useAppSelector(
    (state) => state.config.restaurantConfig
  );
  // Memoized: `useAppSelector` compares by reference, so an inline `.map()`
  // would hand back a new array on every dispatch and re-render this provider —
  // and with it every consumer — throughout a cart edit.
  const loadedTierIds = useAppSelector(selectLoadedTierIds);
  const handleAction = usePromoActionHandler();

  /**
   * The restaurant's own download-app cooldown, in hours — the same value and
   * the same 12h default the pre-migration path used
   * (`CategoryScreen`'s `downloadAppPopupInterval || 12`). Without it the ledger
   * falls back to its 7-day ceiling and a restaurant asking for 12h gets a week.
   */
  const downloadAppCooldownHours =
    restaurantConfig?.showDownloadAppPopup?.downloadAppPopupInterval || 12;

  /**
   * One ask at a time, app-wide: a stage change and a login landing together
   * would otherwise stack two dialogs.
   */
  const inFlight = useRef(false);

  /** Resolves when the modal we opened is no longer the one on screen. */
  const awaitDismissal = useRef<(() => void) | null>(null);

  const showAndAwait = useCallback(
    (component: string, componentProps: Record<string, unknown>) =>
      new Promise<void>((resolve) => {
        awaitDismissal.current = resolve;
        handleModal(true, component, { componentProps });
      }),
    [handleModal]
  );

  /** Fire-and-forget, and only for the durable kinds — see durableDisplayOf. */
  const reportDisplay = useCallback(
    async (directive: PromoPopupDirective, stage: PopupStage) => {
      const durable = durableDisplayOf(directive);
      if (!durable) {
        return;
      }
      try {
        await postPromoPopupDisplay({
          type: directive.type,
          stage,
          channel,
          franchiseId: selectedFranchiseId,
          ...durable,
        });
      } catch (error) {
        // Bookkeeping only: a lost record means the customer may see this
        // pop-up once more, which beats failing the screen they are on.
        console.error(
          `Promo pop-ups: recording ${directive.type} failed`,
          error
        );
      }
    },
    [channel, selectedFranchiseId]
  );

  /**
   * The two below are dependencies of `askForStage`, so they are memoized and
   * declared first: a plain per-render function would pin whichever render
   * last changed one of askForStage's own deps, and every later ask would keep
   * sending that render's `franchiseId` and `loadedTierIds`.
   */
  const fetchDirectives = useCallback(
    async (
      stage: PopupStage,
      options?: AskForStageOptions
    ): Promise<PromoPopupDirective[]> => {
      // One read: two calls meant two localStorage passes per request, and two
      // answers that could in principle disagree.
      const shown = shownParam(downloadAppCooldownHours);

      try {
        const response = await getPromoPopups({
          stage,
          channel,
          franchiseId: selectedFranchiseId,
          ...(mappedOrderType && { orderType: mappedOrderType }),
          ...(options?.rewardApplied !== undefined && {
            rewardApplied: options.rewardApplied,
          }),
          ...(options?.advertisedOfferId && {
            advertisedOfferId: options.advertisedOfferId,
          }),
          ...(shown && { shown }),
        });

        return response.data?.popups ?? [];
      } catch (error) {
        // Includes the proxy's FeatureNotEnabled / Misconfigured 4xxs. A pop-up is
        // never worth an error surface — the screen behind it is the product.
        console.error(`Promo pop-ups: ${stage} request failed`, error);

        return [];
      }
    },
    [channel, mappedOrderType, selectedFranchiseId, downloadAppCooldownHours]
  );

  const show = useCallback(
    async (directive: PromoPopupDirective, stage: PopupStage) => {
      const resolution = resolvePromoPopup(directive, offers ?? [], {
        downloadAppImage:
          restaurantConfig?.showDownloadAppPopup?.downloadAppPopupImage,
        restaurantId,
        loadedTierIds,
      });

      if (resolution.kind === PromoPopupResolutionKind.NOTHING) {
        console.info(
          `Promo pop-ups: skipped ${directive.type} — ${resolution.reason}`
        );

        return false;
      }

      // Recorded the moment it becomes visible, not when it is dismissed: the
      // rules are about having *seen* it.
      recordDisplay(directive.type);
      // Not awaited: bookkeeping must not delay the pixels. It never rejects.
      reportDisplay(directive, stage);

      await showAndAwait(resolution.component, {
        ...resolution.componentProps,
        onCta: () => {
          handleAction(directive.action);
          handleModal(false, "", {});
        },
      });

      return true;
    },
    [
      offers,
      restaurantConfig?.showDownloadAppPopup?.downloadAppPopupImage,
      restaurantId,
      loadedTierIds,
      showAndAwait,
      handleAction,
      handleModal,
      reportDisplay,
    ]
  );

  const askForStage = useCallback(
    async (stage: PopupStage, options?: AskForStageOptions) => {
      // Restaurants opt in: until the flag is on, the app runs its own pop-ups
      // and must not ask, so a half-migrated restaurant cannot show both.
      if (restaurantConfig?.useLoyaltyPopups !== true) {
        return false;
      }
      if (inFlight.current) {
        return false;
      }
      inFlight.current = true;

      try {
        let shownAny = false;

        for (let round = 0; round < MAX_SIGN_IN_ROUNDS; round += 1) {
          const directives = await fetchDirectives(stage, options);
          let shownThisRound = false;

          for (const directive of directives) {
            const shown = await show(directive, stage);
            shownThisRound = shownThisRound || shown;
          }

          shownAny = shownAny || shownThisRound;
          // Every other stage answers completely in one call. A round that
          // showed nothing means the flow is spent — and the display just
          // recorded is what moves the server on when we do ask again.
          if (stage !== PopupStage.SIGN_IN || !shownThisRound) {
            break;
          }
        }

        return shownAny;
      } finally {
        inFlight.current = false;
      }
    },
    [restaurantConfig?.useLoyaltyPopups, fetchDirectives, show]
  );

  // Stable identity: a fresh literal here re-renders every consumer — and the
  // consumers are CategoryScreen, CartScreen and usePayScreen.
  const value = useMemo(() => ({ askForStage }), [askForStage]);

  return (
    <PromoPopupContext.Provider value={value}>
      <PromoPopupDismissalWatcher pending={awaitDismissal} />
      <PromoPopupSignInWatcher askForStage={askForStage} />
      {children}
    </PromoPopupContext.Provider>
  );
};

/**
 * Turns the modal slot's close into the promise resolution [showAndAwait] waits
 * on. Split out so the provider itself does not re-render on every modal change.
 */
const PromoPopupDismissalWatcher = ({
  pending,
}: {
  pending: React.MutableRefObject<(() => void) | null>;
}) => {
  const modal = useAppSelector((state) => state.modal.modal);
  const wasOpen = useRef(false);

  React.useEffect(() => {
    const isOpen = modal?.isOpen === true;
    // Only a falling edge counts. Waiting for the open first is what stops the
    // promise resolving against the state the modal had before we opened ours.
    if (isOpen) {
      wasOpen.current = true;

      return;
    }
    if (wasOpen.current) {
      wasOpen.current = false;
      const resolve = pending.current;
      pending.current = null;
      resolve?.();
    }
  }, [modal?.isOpen, pending]);

  return null;
};

/**
 * Asks the SIGN_IN stage when a guest becomes a customer.
 *
 * Watched here rather than in the login screen because the web has several ways
 * out of that flow — plain login, checkout, the wallet pass, an OTP deep link —
 * and instrumenting one would miss the rest. Signing in is a state change, so
 * this reads it as one.
 *
 * It waits for `orderDataLoading` to settle first: the directives name offers by
 * uuid, resolved against the customer's own list, which is still the guest's
 * until the post-login fetch lands. Asking too early would resolve to nothing —
 * harmless (nothing is recorded) but the customer would miss the pop-up.
 */
const PromoPopupSignInWatcher = ({
  askForStage,
}: {
  askForStage: PromoPopupContextValue["askForStage"];
}) => {
  const isSignedIn = useAppSelector((state) => Boolean(state.userInfo.token));
  const offersLoading = useAppSelector(
    (state) => state.globalComponentsToggleSlice.orderDataLoading
  );
  const wasSignedIn = useRef(isSignedIn);
  const pending = useRef(false);

  React.useEffect(() => {
    if (isSignedIn && !wasSignedIn.current) {
      pending.current = true;
    }
    wasSignedIn.current = isSignedIn;
  }, [isSignedIn]);

  React.useEffect(() => {
    if (!pending.current || offersLoading) {
      return;
    }
    pending.current = false;
    askForStage(PopupStage.SIGN_IN);
  }, [isSignedIn, offersLoading, askForStage]);

  return null;
};

export const usePromoPopups = () => useContext(PromoPopupContext);
